<template>
  <article class="agenda-list">
    <template v-if="groupedEvents.length > 0">
      <section
        v-for="year in groupedEvents"
        :key="`agenda-list-${year.year}`"
        :class="isWhen('year', year) === 'past' ? 'agenda-list__year--past' : ''"
        class="agenda-list__year"
      >
        <div
          v-for="month of year.months"
          :key="`agenda-list-${year.year}-${month.month}`"
          class="agenda-list__month"
        >
          <h5 class="agenda-list__month-name">
            {{ monthName(month.month) }} <!-- - {{ year.year }} -->
          </h5>

          <transition-group name="fade">
            <div
              v-for="day of month.days"
              :key="`agenda-list-${year.year}-${month.month}-${day.day}`"
              class="agenda-list__day-wrapper"
            >
              <div class="agenda-list__day-day">
                <span class="agenda-list__day-week">
                  {{ dayOfWeek(year.year, month.month, day.day) }}
                </span>

                <span
                  :class="[isWhen('day', year.year, month.month, day.day)]"
                  class="agenda-list__day-date"
                >
                  {{ day.day }}
                </span>
              </div>

              <div
                v-for="(event, i) of day.events"
                :key="`agenda-list-event-${event.id}-${i}`"
                :class="[
                  ...event.classes,
                  isWhen('day', year.year, month.month, day.day)
                ]"
                class="agenda-event"
                @click="$emit('event-callback', $event, $moment().toISOString(), event)"
              >
                <div class="agenda-event__title">
                  {{ event['user'].name || event['special_patient'].name }}
                </div>

                <div class="agenda-event__subtitle">
                  <span class="agenda-event__type">
                    {{ event['medical_consultation_type'] | toExam }}
                  </span>

                  <span>&#x2022;</span>

                  <span class="agenda-event__time">
                    {{ $moment(event['appointment_start']).format('HH:mm') }}
                    às
                    {{ $moment(event['appointment_finish']).format('HH:mm') }}
                  </span>
                </div>
              </div>
            </div>
          </transition-group>
        </div>
      </section>
    </template>

    <transition name="fade">
      <div class="agenda-list__load-more">
        <button
          v-if="(pagination.totalPages > 0) && (pagination.currentPage !== pagination.totalPages)"
          class="btn btn-primary"
          @click="loadMore()"
        >
          Carregar mais
        </button>
      </div>
    </transition>

    <transition name="fade">
      <h5 v-if="events.length === 0" class="agenda-list__empty">
        Não foram encontrados agendamentos no período selecionado.
      </h5>
    </transition>

    <transition name="fade">
      <dr-loader v-if="loading" class="agenda-list__loader" size="lg" />
    </transition>

    <button
      @click="$emit('event-callback', $event, $moment().toISOString())"
      class="btn btn-primary agenda-list__new"
    >
      +
    </button>
  </article>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'DrAgendaLista',

  filters: {
    toExam (value) {
      if (/exame|procedimento/ig.test(value)) {
        return 'Exame/procedimento'
      } else {
        return value.trim()
      }
    }
  },

  props: {
    selectedDate: {
      type: String,
      required: false,
      default: () => (new Date()).toISOString()
    }
  },

  data () {
    return {
      itemsPerPage: 10,
      loading: false,
      groupedEvents: [],
      events: [],
      pagination: {
        totalRows: 0,
        totalPages: 0,
        currentPage: 1
      }
    }
  },

  computed: {
    ...mapGetters({
      office: 'getCurrentOffice'
    }),

    prefix () {
      return `/${this.fn_GET_PARENT_ROUTE(this.$route)}/doctor_office/${this.office.id}`
    },

    filter () {
      const initialDate = this.$moment(this.selectedDate).format('YYYY-MM-DD')
      return `filterGreatherEqual[a.appointmentStart]=${initialDate} 00:00:00&itensPerPage=${this.itemsPerPage}`
    }
  },

  watch: {
    selectedDate: {
      deep: false,
      immediate: false,
      handler () {
        this.fetchData()
      }
    },

    office: {
      deep: true,
      immediate: false,
      handler () {
        this.fetchData()
      }
    }
  },

  created () {
    this.fetchData()
  },

  methods: {
    fetchData () {
      const url = `${this.prefix}/agenda?${this.filter}&page=${this.pagination.currentPage}`

      this.loading = true
      this.fn_SHOW_PROGRESS()

      this.$http.get(url, this.$store.getters.getAuthHeader)
        .then((res) => {
          const { data } = res.data
          data.forEach((event) => {
            if (!this.events.find(e => e.id === event.id)) {
              this.events.push(event)
            }
          })

          this.pagination = { ...res.data.pagination }

          if (this.events.length > 0) {
            this.filterOutEvents()
            this.orderEvents()
            this.setCSSClasses()
            this.groupData()
          }
        })
        .catch((err) => {
          console.log('err \n', err)
        })
        .finally(() => {
          this.loading = false
          this.fn_HIDE_PROGRESS()
        })
    },

    filterOutEvents () {
      let filteredEvents = [...this.events]
      const notAllowed = [
        /(cancelado)/i,
        /(não|nao)\s(aprovado)/i,
        /(não|nao)\s(compareceu)/i
      ]

      // Filtra status não permitidos
      filteredEvents = filteredEvents.filter((e) => {
        return notAllowed
          .map(regex => regex.test(e.status.trim()))
          .every(t => !t)
      })

      // Filtra agendamentos do tipo Dr. Conecta com status Aguardando pagamento
      filteredEvents = filteredEvents
        .filter((e) => {
          return !(/drconecta/i.test(e.type) &&
            /(aguardando)\s?(pagamento)/i.test(e.status))
        })

      this.events = [...filteredEvents]
    },

    orderEvents () {
      this.events.sort((a, b) => {
        const aDate = this.$moment(a['appointment_start'])
        const bDate = this.$moment(b['appointment_start'])

        if (this.$moment(aDate).isBefore(bDate)) {
          return -1
        }

        if (this.$moment(aDate).isAfter(bDate)) {
          return 1
        }

        return 0
      })
    },

    setCSSClasses () {
      this.events.forEach((event) => {
        const classes = []

        if (event.status === 'REALIZADO') {
          classes.push('consulta')
          classes.push('realizado')
        } else {
          if (event['medical_consultation_type']) {
            classes.push(
              (/(exame|procedimento)/gi.test(String(event['medical_consultation_type'])))
                ? 'exame'
                : String(event['medical_consultation_type']).toLowerCase()
            )
          }
        }

        if (/aberto/gi.test(event.status) && /(particular(_\w+)?)|convenio/gi.test(event.type)) {
          classes.push('confirmado')
        }

        if (/drconecta/gi.test(event.type)) {
          classes.push('drconecta')
        }

        // Adiciona classe indicativa de pagamento realizado
        if (event['financial_statement']['is_consolidated']) {
          classes.push('pagamento-confirmado')
        }

        const subtitle = (/(exame|procedimento)/gi.test(String(event['medical_consultation_type'])))
          ? 'exame/procedimento'
          : String(event['medical_consultation_type']).toLowerCase()

        if (/cancelado/gi.test(event.status)) {
          classes.push('cancelado')
        }

        // Adiciona classe indicativa de consulta telemedicina
        if (/telemedicina/gi.test(event.type)) {
          const indexes = []
          indexes.push(classes.findIndex(c => c === 'exame'))
          indexes.push(classes.findIndex(c => c === 'retorno'))
          indexes.push(classes.findIndex(c => c === 'consulta'))

          indexes.forEach((i) => {
            if (i > -1) {
              classes.splice(i, 1)
            }
          })

          classes.push('telemedicina')
        }

        event.classes = classes
      })
    },

    groupData () {
      const groupedEvents = []
      const years = [
        ...new Set(
          this.events.map(e => this.$moment(e['appointment_start']).format('YYYY'))
        )
      ]
      const months = [
        ...new Set(
          this.events.map(e => this.$moment(e['appointment_start']).format('YYYY-MM'))
        )
      ]
      const days = [
        ...new Set(
          this.events.map(e => this.$moment(e['appointment_start']).format('YYYY-MM-DD'))
        )
      ]

      years.forEach(year => groupedEvents.push({ year, months: [] }))

      months.forEach((month) => {
        const [y, m] = month.split('-')
        const year = groupedEvents.find(e => e.year === y)
        year.months.push({ month: m, days: [] })
      })

      days.forEach((day) => {
        const [y, m, d] = day.split('-')
        const year = groupedEvents.find(e => e.year === y)
        const month = year.months.find(e => e.month === m)
        month.days.push({ day: d, events: [] })
      })

      groupedEvents.forEach((y) => {
        y.months.forEach((m) => {
          m.days.forEach((d) => {
            const search = `${y.year}-${m.month}-${d.day}`
            d.events = this.events.filter(e => {
              return this.$moment(e['appointment_start']).isSame(search, 'day')
            })
          })
        })
      })

      this.groupedEvents = groupedEvents
    },

    loadMore () {
      if (this.pagination.currentPage !== this.pagination.totalPages) {
        this.pagination.currentPage += 1
        this.fetchData()
      }
    },

    monthName (value) {
      const m = parseInt(value, 10)
      return this.$moment().set('month', m - 1).format('MMMM')
    },

    dayOfWeek (year, month, day) {
      const date = `${year}-${month}-${day}`
      return this.$moment(date).format('ddd')
    },

    isWhen (unity, year, month = 1, day = 1) {
      const date = `${year}-${month}-${day}`
      const now = this.$moment()
      const when = this.$moment(date, 'YYYY-MM-DD')

      if (this.$moment(when).isBefore(now, unity)) {
        return 'past'
      }

      if (this.$moment(when).isSame(now, unity)) {
        return 'now'
      }

      if (this.$moment(when).isAfter(now, unity)) {
        return 'future'
      }
    }
  }
}
</script>
